import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useMutation as useApolloMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { LockIcon, GlobeIcon } from 'lucide-react';
import { isBefore, startOfDay, endOfDay } from 'date-fns';

import { Button } from '@epoch-ui/components/Button/button';
import { SheetHeader, SheetTitle, SheetFooter } from '@epoch-ui/components/Sheet/sheet';
import { Form, InputField, CoverPhotoField, CheckboxField } from '@epoch-ui/components/Form';
import { ErrorMessage } from '@epoch-ui/components/ErrorMessage';
import { ButtonLink } from '@epoch-ui/components/ButtonLink';
import { DateTimePickerField } from '@epoch-ui/components/Form/fields/DateTimePickerField/DateTimePickerField';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  ActionButtonCaption
} from '@epoch-ui/components/ActionButton';
import {
  guessTimezone,
  getStartOfDayInTimezone,
  interpretDateInTimezone
} from 'utils/dateTimeUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { ZOOM_SYNC_FAILED_ERROR } from 'constants/strings';
import { EVENT_PHOTO_MUTATION } from 'graphql/mutations/file-upload';
import { queryClient } from '@api/queryClient';
import { EventDescriptionField } from '@event-editor/../components/EventDescriptionField';
import { EventFormatFieldsSection } from '@event-editor/../components/EventFormatFieldsSection';
import { TimezoneSelectorField } from '@event-editor/../components/TimezoneSelectorField';
import { EventTeamField } from '@event-editor/../components/EventTeamField';
import { EventLocationField } from '@event-editor/../components/EventLocationField';
import { eventBasicDetailsApi } from '@event-editor/api/eventBasicDetails';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useEditEventDetailsSheet,
  useSchedulePublishModal
} from '@event-editor/hooks/eventEditorState';
import { isStartTimeBeforeScheduledPublish } from '@event-editor/utils';
import {
  convertSendAtToDBValue,
  convertSendAtToFormValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import {
  useEventUpdateNotificationsModal,
  EventUpdateNotificationSettingsModal
} from '@event-editor/components/EventUpdateNotificationSettingsModal';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { EventDomainField } from '@event-editor/../components/EventDomainField';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { T2_DOMAIN } from 'constants/featureFlags';

import { SelectEventVisibilityModal } from './SelectEventVisibilityModal';
import {
  getFormValueFromApi,
  editEventDetailsSchemaFactory,
  formatSingleEventFormValuesForApi,
  EditEventDetailsFormValue
} from './schemaUtils';
import { useEventTimeChangeValidations } from './validateEventTimeChanges';
import { TimeChangeWarningBanner } from './TimeChangeWarningBanner';
import { TimeChangeConfirmModal, TimeChangeConfirmModalType } from './TimeChangeConfirmModal';

export const EditSingleEventBasicDetailsForm = (): React.ReactElement => {
  const t2DomainEnabled = useFeatureFlag(T2_DOMAIN);

  const event = useEditableEvent();
  const editEventDetailsSheetState = useEditEventDetailsSheet();
  const schedulePublishModal = useSchedulePublishModal();
  const { showToast } = useToaster();
  const updateNotificationModal = useEventUpdateNotificationsModal({
    submitHandler: (notificationSettings) => {
      updateEventDetailsMutation.mutate({
        eventPayload: formatSingleEventFormValuesForApi(event.id!, form.getValues()),
        updateNotificationSettings: notificationSettings
      });
      updateNotificationModal.close();
    }
  });

  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);
  const [timeChangeConfirmModalType, setTimeChangeConfirmModalType] =
    useState<TimeChangeConfirmModalType | null>(null);

  const [uploadEventPhoto] = useApolloMutation(EVENT_PHOTO_MUTATION);
  const { mutateAsync: fetchEventChanges, isPending: fetchingEventChanges } = useMutation({
    mutationFn: eventBasicDetailsApi.getChanges,
    onError: (error) => {
      console.error('Failed to fetch event changes:', error);
      showToast('Event couldn’t be saved. Please try again later.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const updateEventDetailsMutation = useMutation({
    mutationFn: eventBasicDetailsApi.update,
    onSuccess: async (result) => {
      await queryClient.refetchQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
      editEventDetailsSheetState.close();
      if (result.zoomSyncFailed) {
        showToast(ZOOM_SYNC_FAILED_ERROR, { variant: ToasterVariant.ERROR, keepOpen: true });
      } else {
        showToast('Event details updated successfully');
      }
    },
    onError: (error) => {
      console.error('Failed to update event details:', error);
      showToast('Failed to update event details. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const form = useForm<EditEventDetailsFormValue>({
    resolver: zodResolver(editEventDetailsSchemaFactory(event, { t2DomainEnabled })),
    mode: 'onChange',
    values: getFormValueFromApi(event) as EditEventDetailsFormValue
  });

  // Effect to handle cross-field validations for startDateTime and endDateTime
  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'startDateTime' || name === 'endDateTime') {
        form.trigger(['startDateTime', 'endDateTime']);
      }
    });

    return (): void => subscription.unsubscribe();
  }, [form]);

  const [allDay, isPrivate, timezoneCode, updatedStartDateTime, updatedEndDateTime] = form.watch([
    'allDay',
    'isPrivate',
    'timezoneCode',
    'startDateTime',
    'endDateTime'
  ]);

  // Compute validation warnings when times change
  const timeChangeValidations = useEventTimeChangeValidations(
    updatedStartDateTime,
    updatedEndDateTime,
    timezoneCode
  );

  const hasTimeChangeValidations = timeChangeValidations.length > 0;

  // Check if start time is before scheduled publish time (component-level validation)
  const hasStartTimeBeforeScheduledPublishError = React.useMemo(() => {
    if (!updatedStartDateTime || !timezoneCode) {
      return false;
    }
    const startDateTimeUTC = convertSendAtToDBValue(updatedStartDateTime, timezoneCode);
    return isStartTimeBeforeScheduledPublish(startDateTimeUTC, event.publishSchedule);
  }, [updatedStartDateTime, timezoneCode, event.publishSchedule]);

  const disablePastDates = useCallback(
    (date: Date): boolean => {
      const targetTz = timezoneCode ?? guessTimezone();
      const today = getStartOfDayInTimezone(targetTz);
      const dateInTargetTz = interpretDateInTimezone(date, targetTz);
      return isBefore(dateInTargetTz, today);
    },
    [timezoneCode]
  );

  const handleResetTime = useCallback((): void => {
    const originalStartDateTime = event.datetimeScheduled
      ? convertSendAtToFormValue(event.datetimeScheduled, event.timezoneCode ?? guessTimezone())
      : undefined;
    const originalEndDateTime = event.datetimeEnd
      ? convertSendAtToFormValue(event.datetimeEnd, event.timezoneCode ?? guessTimezone())
      : undefined;

    if (originalStartDateTime) {
      form.setValue('startDateTime', originalStartDateTime, {
        shouldValidate: true,
        shouldDirty: false
      });
    }
    if (originalEndDateTime) {
      form.setValue('endDateTime', originalEndDateTime, {
        shouldValidate: true,
        shouldDirty: false
      });
    }

    if (event.timezoneCode) {
      form.setValue('timezoneCode', event.timezoneCode, {
        shouldValidate: true,
        shouldDirty: false
      });
    }
    form.setValue('allDay', event.allDay ?? false, { shouldValidate: true, shouldDirty: false });

    setTimeChangeConfirmModalType(null);
  }, [event, form]);

  const handleConfirmTimeChange = useCallback(async (): Promise<void> => {
    setTimeChangeConfirmModalType(null);
    await proceedWithSave(form.getValues());
  }, [form]);

  const onSubmit = async (data: EditEventDetailsFormValue): Promise<void> => {
    // Check if there are time change validations - if so, show confirmation modal
    if (hasTimeChangeValidations) {
      setTimeChangeConfirmModalType(TimeChangeConfirmModalType.CONFIRM);
      return;
    }

    // Proceed with normal save flow
    await proceedWithSave(data);
  };

  const proceedWithSave = async (data: EditEventDetailsFormValue): Promise<void> => {
    if (event.isPublished && (event?.totalGuests ?? 0) > 0) {
      // Check if event changes require sending update notifications
      const eventChanges = await fetchEventChanges({
        eventId: event.id!,
        payload: formatSingleEventFormValuesForApi(event.id!, form.getValues())
      });

      if (eventChanges?.shouldRequestSendUpdateNotificationOptions) {
        updateNotificationModal.open({
          canResetRsvps: eventChanges.canResetRsvps
        });

        return;
      }
    }

    // Update event details
    updateEventDetailsMutation.mutate({
      eventPayload: formatSingleEventFormValuesForApi(event.id!, data)
    });
  };

  const isLoading = fetchingEventChanges || updateEventDetailsMutation.isPending;

  return (
    <React.Fragment>
      <SheetHeader className="tw-p-6">
        <SheetTitle className="tw-mb-0">Edit event</SheetTitle>
      </SheetHeader>
      <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto tw-pb-6">
        <Form {...form}>
          <form
            id="edit-event-details-form"
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              console.error(errors);
            })}
            className="tw-flex tw-flex-col tw-gap-6 tw-px-6"
          >
            <CoverPhotoField
              name="photoUrl"
              className="tw-mx-auto tw-aspect-square tw-max-h-[308px] tw-w-auto"
              onUploadComplete={(file) =>
                uploadEventPhoto({ variables: { file, isCoverPhoto: true } }).then(
                  (res) => res.data?.uploadEventPhoto?.photoUrl
                )
              }
            />

            <InputField name="title" label="Title" placeholder="Enter the title" />

            <div className="tw-flex tw-flex-col tw-gap-4">
              <div>
                <DateTimePickerField
                  name="startDateTime"
                  label="Start time"
                  allDay={allDay}
                  disabled={event.hasStarted}
                >
                  <DateTimePickerField.DateInput disabled={disablePastDates} />
                  <DateTimePickerField.Time interval={5} />
                  <TimezoneSelectorField name="timezoneCode" valueFormat="abbreviation" />
                </DateTimePickerField>
                {hasStartTimeBeforeScheduledPublishError && (
                  <ErrorMessage>
                    Start time cannot be before the event is scheduled to be published.{' '}
                    <ButtonLink
                      onClick={() => schedulePublishModal.open({ isReschedule: true })}
                      className="tw-text-xs tw-font-normal tw-underline"
                    >
                      Update schedule
                    </ButtonLink>
                  </ErrorMessage>
                )}
              </div>
              <DateTimePickerField name="endDateTime" label="End time" allDay={allDay}>
                <DateTimePickerField.DateInput disabled={disablePastDates} />
                <DateTimePickerField.Time interval={5} />
                <TimezoneSelectorField name="timezoneCode" valueFormat="abbreviation" readOnly />
              </DateTimePickerField>
              <div>
                <CheckboxField
                  name="allDay"
                  label="All day"
                  className="tw-mt-0"
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const startDateTime = form.getValues('startDateTime');
                      const endDateTime = form.getValues('endDateTime');

                      if (startDateTime) {
                        form.setValue('startDateTime', startOfDay(startDateTime), {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }

                      if (endDateTime) {
                        form.setValue('endDateTime', endOfDay(endDateTime), {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }
                    }
                  }}
                />
                {hasTimeChangeValidations && (
                  <TimeChangeWarningBanner
                    onClick={(): void =>
                      setTimeChangeConfirmModalType(TimeChangeConfirmModalType.DISPLAY)
                    }
                  />
                )}
              </div>
            </div>

            <EventFormatFieldsSection eventId={event.id} />

            <EventDescriptionField
              name="description"
              label="Description"
              eventTitle={form.watch('title')}
              compactToolbar
            />

            <ActionButton onClick={(): void => setIsVisibilityModalOpen(true)}>
              <ActionButtonIcon theme="success">
                {isPrivate ? <LockIcon /> : <GlobeIcon />}
              </ActionButtonIcon>
              <ActionButtonLabel>
                Visibility
                <ActionButtonCaption>{isPrivate ? 'Private' : 'Public'}</ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>

            {t2DomainEnabled && (
              <EventDomainField name="t2DomainArray" disabled={event.isPublished} />
            )}

            <EventTeamField name="teamId" />

            <EventLocationField name="companyLocationId" />

            <EventUpdateNotificationSettingsModal {...updateNotificationModal.modalProps} />
          </form>
        </Form>
      </div>

      <hr className="tw-my-0 tw-w-full tw-border-gray-300" />
      <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-4">
        <Button
          form="edit-event-details-form"
          type="submit"
          variant="primary"
          className="tw-h-10 tw-rounded-lg tw-p-3"
          loading={isLoading}
          disabled={!form.formState.isDirty || isLoading || hasStartTimeBeforeScheduledPublishError}
        >
          Save
        </Button>
      </SheetFooter>

      <SelectEventVisibilityModal
        open={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        initialValue={isPrivate ? 'private' : 'public'}
        onSave={(visibility): void => {
          form.setValue('isPrivate', visibility === 'private', {
            shouldValidate: true,
            shouldDirty: true
          });

          setIsVisibilityModalOpen(false);
        }}
      />

      <TimeChangeConfirmModal
        open={timeChangeConfirmModalType !== null}
        onOpenChange={(open): void => {
          if (!open) {
            setTimeChangeConfirmModalType(null);
          }
        }}
        timeChangeValidations={timeChangeValidations}
        type={timeChangeConfirmModalType ?? TimeChangeConfirmModalType.DISPLAY}
        onReschedule={handleResetTime}
        onConfirm={handleConfirmTimeChange}
      />
    </React.Fragment>
  );
};
